// User.types.ts
import type { Role } from "./role";
import type { Permission } from "./permission";
import { AuditFields, CommonFields, CommonStatus, PartnerRef, ValidAny } from "./common";
import { ISOString } from "./date";

/**
 * User status enum
 */
export enum UserStatus {
  ACTIVE = CommonStatus.ACTIVE,
  INACTIVE = CommonStatus.INACTIVE,
  PENDING = CommonStatus.PENDING,
  SUSPENDED = CommonStatus.SUSPENDED,
  DISABLED = CommonStatus.DISABLED,
}

/**
 * Account type enum
 */
export type AccountType = "employee" | "contractor" | "guest" | "service" | "external";

/**
 * A user's role assignment.
 *
 * This is the shape returned by `GET /users/{id}/roles`, which emits `role`,
 * `assigned_datetime`, `is_inherited`, and (when set) `expires_datetime` /
 * `scope`. The remaining fields (`role_id_ref`, `assigned_by_user_id_ref`,
 * `is_expired`) only appear on the embedded `assigned_roles` shape, so they
 * are optional here. Derive expiry on the client from `expires_datetime`.
 */
export interface UserRoleAssignment {
  role: Role;
  assigned_datetime?: ISOString;
  expires_datetime?: ISOString | null;
  scope?: string | null;
  is_inherited?: boolean;
  role_id_ref?: string;
  assigned_by_user_id_ref?: string;
  is_expired?: boolean;
}

/**
 * Base user interface - fields that are in both create and read
 */
interface UserBase extends PartnerRef {
  email: string;
  full_name?: string | null;
  preferred_name?: string | null;
  phone?: string | null;
  department?: string | null;
  job_title?: string | null;
  manager_user_id_ref?: string | null;
  timezone: string; // Default: "America/Chicago"
  locale: string; // Default: "en-US"
  preferred_language: string; // Default: "en"
  status: UserStatus; // Default: pending
  account_type: AccountType; // Default: employee
  assigned_roles?: UserRoleAssignment[];
  attributes?: Record<string, ValidAny>;
}

/**
 * Full user record from the API - includes server-managed fields
 */
export interface User extends UserBase, CommonFields, AuditFields {
  // ReadOnly fields from IdP
  unique_name?: string | null;
  user_principal_name?: string | null;

  // ReadOnly verification status
  email_verified: boolean;
  phone_verified: boolean;

  // ReadOnly expandable fields
  effective_permissions?: Permission[];
  effective_permissions_last_updated?: ISOString | null;
  manager?: User | null;

  // ReadOnly: role names aggregated by list endpoints (at-a-glance display)
  assigned_role_names?: string[];

  // ReadOnly activity tracking
  last_login_datetime?: string | null;
}

/**
 * Payload for creating a new user
 */
export interface UserCreate extends UserBase {}

/**
 * Payload for updating a user - all fields optional except id
 */
export interface UserUpdate extends Partial<UserBase> {
  id: string;
}
